import { useSetAtom, useAtomValue } from 'jotai';
import React__default, { useMemo, useRef, useEffect } from 'react';
import { userAtom, accessTokenAtom, isAuthenticatedAtom, meAtom, isAuthLoadingAtom, authErrorAtom, clearAuthStateAtom, clearAuthErrorAtom } from '../atoms/authAtoms.js';
import { clearMfaChallengeAtom } from '../atoms/mfaChallengeAtoms.js';
import { useGetMe } from '../hooks/useGetMe.js';
import { AuthService } from '../services/authService.js';
import { MfaSmsService } from '../services/mfaSmsService.js';
import { AuthError } from '../types/auth.js';
import { AuthContext } from './AuthContext.js';

/**
 * AuthSessionProvider manages Firebase Auth state and /me API calls
 * - Watches Firebase Auth state changes
 * - Calls GET /me after login or session restore
 * - Caches { user, me, isLoading, error } in atoms
 * - Handles auth transitions cleanly (401 → sign out, null user → clear me)
 */
function AuthSessionProvider({ auth, apiClient, queryClient, queryKeys, onUnauthorized, meEndpoint, children, }) {
    // Create service instances
    const authService = useMemo(() => new AuthService(auth), [auth]);
    const mfaSmsService = useMemo(() => new MfaSmsService(auth), [auth]);
    // Create context value
    const contextValue = useMemo(() => ({
        auth,
        apiClient,
        queryClient,
        queryKeys,
        authService,
        mfaSmsService,
    }), [auth, apiClient, queryClient, queryKeys, authService, mfaSmsService]);
    const setUser = useSetAtom(userAtom);
    const setAccessToken = useSetAtom(accessTokenAtom);
    const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
    const setMe = useSetAtom(meAtom);
    const setLoading = useSetAtom(isAuthLoadingAtom);
    const setError = useSetAtom(authErrorAtom);
    const clearAuthState = useSetAtom(clearAuthStateAtom);
    const clearAuthError = useSetAtom(clearAuthErrorAtom);
    const clearMfaChallenge = useSetAtom(clearMfaChallengeAtom);
    // Track if we're handling a 401 to prevent redirect loops
    const isHandling401Ref = useRef(false);
    // Watch Firebase Auth state and call /me when user exists
    useEffect(() => {
        setLoading(true);
        clearAuthError();
        const unsubscribe = authService.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    const token = await user.getIdToken();
                    apiClient.setAuthToken(token);
                    apiClient.setTokenRefreshCallback(async () => {
                        const newToken = await user.getIdToken(true);
                        if (newToken) {
                            apiClient.setAuthToken(newToken);
                            return newToken;
                        }
                        return null;
                    });
                    setUser(user);
                    setAccessToken(token);
                    setIsAuthenticated(true);
                    setError(null);
                }
                catch (error) {
                    console.error('Failed to get user token:', error);
                    clearAuthState();
                    setLoading(false);
                }
            }
            else {
                clearAuthState();
                clearMfaChallenge();
                apiClient.removeAuthToken();
                apiClient.removeTokenRefreshCallback();
                setLoading(false);
            }
        });
        return () => {
            unsubscribe();
            apiClient.removeTokenRefreshCallback();
        };
    }, [
        setUser,
        setAccessToken,
        setIsAuthenticated,
        setError,
        clearAuthState,
        clearAuthError,
        clearMfaChallenge,
        setLoading,
        authService,
        apiClient,
    ]);
    // Call /me when Firebase auth user exists
    const user = useAtomValue(userAtom);
    const { data: me, error: meError, isLoading: meLoading, } = useGetMe(!!user, meEndpoint, apiClient, queryKeys);
    useEffect(() => {
        if (me) {
            setMe(me);
            setError(null);
        }
    }, [me, setMe, setError]);
    useEffect(() => {
        if (user) {
            setLoading(meLoading);
        }
    }, [user, meLoading, setLoading]);
    useEffect(() => {
        if (!meError)
            return;
        const axiosError = meError;
        const status = axiosError.response?.status;
        if (status === 401) {
            if (!isHandling401Ref.current) {
                isHandling401Ref.current = true;
                clearAuthState();
                authService.signOut().catch((error) => {
                    console.error('Failed to sign out:', error);
                });
                apiClient.removeAuthToken();
                apiClient.removeTokenRefreshCallback();
                queryClient.removeQueries({ queryKey: queryKeys.me });
                if (onUnauthorized) {
                    onUnauthorized();
                }
                setTimeout(() => {
                    isHandling401Ref.current = false;
                }, 1000);
            }
            return;
        }
        // 403 (or any other error) → store error; routing will decide what to do
        setError(new AuthError(status ? `me/${status}` : 'me/error', meError instanceof Error ? meError.message : 'Failed to load session'));
    }, [
        meError,
        clearAuthState,
        onUnauthorized,
        setError,
        authService,
        apiClient,
        queryClient,
        queryKeys,
    ]);
    useEffect(() => {
        if (!user) {
            setMe(null);
            // Use removeQueries instead of invalidateQueries to clear the cache entirely
            // This ensures fresh data is fetched on re-login (staleTime: Infinity won't serve stale data)
            queryClient.removeQueries({ queryKey: queryKeys.me });
            if (queryKeys.debug) {
                queryClient.removeQueries({ queryKey: queryKeys.debug });
            }
        }
    }, [user, setMe, queryClient, queryKeys]);
    return React__default.createElement(AuthContext.Provider, { value: contextValue }, children);
}

export { AuthSessionProvider };
//# sourceMappingURL=AuthSessionProvider.js.map
